// The data rules behind a wallet rule's orderRule column picker, kept out of the
// component so they can be tested (src/i18n.js uses top-level await).
//
// The cabinet compiles the order on save (cabinet-db WalletOrder, the one parser) and
// refuses one it cannot read; this reads only the plain keys the picker writes —
// `$name[start+count]_2%d asc` — and answers null for anything else (a sum `+`, a
// percentage `^`), which then stays text, so nothing the picker cannot show is
// rewritten by it.

// One key with the whitespace taken out, as the backend reads it.
const KEY = /^\$([a-z0-9_]+?)(?:\[(\d+)\+(\d+)\])?(_[12])?%([dsb])(asc|desc)?$/i;

/**
 * The order's keys, `[{ name, type, failed, range, descending }]`: `range` is
 * `[start, count]` or null for all 30 days. `[]` for no order, null when the text
 * holds something the picker cannot show.
 */
export const parseOrder = (text) => {
    if (text === null || text === undefined || String(text).trim() === '') return [];
    const keys = [];
    for (const written of String(text).split(',')) {
        const match = KEY.exec(written.replace(/\s+/g, ''));
        if (!match) return null;
        const [, name, start, count, slot, type, direction] = match;
        keys.push({
            name: name.toLowerCase(),
            type: `%${type.toLowerCase()}`,
            failed: slot === '_2',
            range: start === undefined ? null : [Number(start), Number(count)],
            descending: (direction || '').toLowerCase() === 'desc'
        });
    }
    return keys;
};

/** One key as the backend reads it, direction always written. */
export const formatKey = ({ name, type, failed, range, descending }) =>
    `$${name}${range ? `[${range[0]}+${range[1]}]` : ''}${failed ? '_2' : ''}${type} ${descending ? 'desc' : 'asc'}`;

/** The keys as an orderRule; '' for none, which is id order. */
export const formatOrder = (keys) => keys.map(formatKey).join(', ');

/** Whether two keys read the same value, whatever their direction: the backend refuses the second. */
export const sameKey = (a, b) =>
    a.name === b.name &&
    a.failed === b.failed &&
    (a.range || []).join('+') === (b.range || []).join('+');

/**
 * Where a column starts when it is picked: the direction (and slot, and days) it is
 * usually wanted with. The rest start lowest first, over all 30 days, successes.
 */
export const ORDER_PRESETS = {
    idle_seconds_wallet: { descending: true },
    status_lasttx_wallet: { descending: true },
    conversion_30_days_wallet: { descending: true },
    count_continuous_wallet: { failed: true },
    count_30_days_wallet: { range: [0, 6] }
};

/** A new key for the column `variable` (as the API lists it). */
export const newKey = (variable) => ({
    name: variable.name,
    type: variable.type,
    failed: false,
    range: null,
    descending: false,
    ...ORDER_PRESETS[variable.name]
});

/** The picker's groups, in order; a column in none of them goes under `other`. */
export const ORDER_GROUPS = [
    ['now', ['count_active_wallet', 'total_amount_active_wallet', 'available_wallet', 'idle_seconds_wallet',
        'status_lasttx_wallet']],
    ['today', ['count_per_day_wallet', 'total_amount_per_day_wallet']],
    ['month', ['count_per_month_wallet', 'total_amount_per_month_wallet']],
    ['days30', ['count_30_days_wallet', 'conversion_30_days_wallet']],
    ['streak', ['count_continuous_wallet']],
    ['wallet', ['age_days_wallet', 'wallet_id']]
];

/** `variables` (as the API lists them) by group: `[{ group, variables }]`, empty groups left out. */
export const groupColumns = (variables) => {
    const byName = new Map(variables.map((v) => [v.name, v]));
    const placed = new Set();
    const groups = ORDER_GROUPS.map(([group, names]) => ({
        group,
        variables: names.filter((name) => byName.has(name)).map((name) => {
            placed.add(name);
            return byName.get(name);
        })
    }));
    groups.push({ group: 'other', variables: variables.filter((v) => !placed.has(v.name)) });
    return groups.filter(({ variables: listed }) => listed.length > 0);
};

/** The day ranges the picker offers: all 30 days, today, the last 3, 7 and 14. */
export const DAY_RANGES = [null, [0, 0], [0, 2], [0, 6], [0, 13]];

/** A range as a select's value: 'all' or 'start+count'. */
export const rangeValue = (range) => (range ? `${range[0]}+${range[1]}` : 'all');

/** The range a select's value names. */
export const rangeOf = (value) => (value === 'all' ? null : value.split('+').map(Number));
